import { DragDropProvider } from '@dnd-kit/react'
import type { DragEndEvent } from '@dnd-kit/react'
import { useDraggable, useDroppable } from '@dnd-kit/react'
import { CONFIG_ELEMENTS, TestAnswerFeedbackDisplayOption } from '@vedalib/editor/lib/elements'
import { rtValueToText, textToRtValue } from '@vedalib/rich-text'
import { useSize, useUpdateEffect } from 'ahooks'
import { produce } from 'immer'
import { useRef } from 'react'
import { useContext } from 'use-context-selector'
import { swapItems } from 'utils'

import { buildTestAnnouncement, useLiveFeedback } from 'components/Editor/EditorElements/courseKit/CourseKitUtils'
import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import { getFeedbackValue } from 'components/Editor/EditorElements/utils/getFeedbackValue'
import { RichTextViewer } from 'components/form/RichText/RichTextViewer'
import { setQuestion } from 'services/Store/Project/actions'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getBlockState } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { createDndAccessibilityPlugin } from 'utils/accessibility'

import { useGetSize } from '../../helper'
import type { MatchingGroups } from '../../testMatchingTypes'
import TestMatchingContext from '../TestMatchingContext'
import * as s from './DefaultElementPreview.module.scss'
import MatchingItem from './MatchingItem'

const RIGHT_PREFIX = 'right-'

const getName = (entity: MatchingGroups | null | undefined): string | undefined => {
  if (!entity) {
    return undefined
  }
  return rtValueToText(entity.label) || entity.value
}

type PreviewSideItemProps = {
  item: MatchingGroups
  height: number | undefined
  index: number
}

const LeftPreviewItem: React.FC<PreviewSideItemProps> = ({ item, height, index }) => {
  const { styles, font, isDisabled } = useContext(TestMatchingContext)
  const { ref, isDragging } = useDraggable({ id: item.value, data: item, disabled: isDisabled })
  const ariaLabel = rtValueToText(item.label) || item.value

  return (
    <MatchingItem
      ariaLabel={ariaLabel}
      font={font}
      height={height}
      index={index}
      isActive={item.isActive}
      isDragged={isDragging}
      mode='left'
      ref={ref as React.RefCallback<HTMLDivElement>}
      styles={styles}
      tabIndex={0}
    >
      <div className={s.text}>
        <RichTextViewer styles={font} value={item.label || textToRtValue('')} />
      </div>
    </MatchingItem>
  )
}

const RightPreviewItem: React.FC<PreviewSideItemProps> = ({ item, height, index }) => {
  const { styles, font, isDisabled } = useContext(TestMatchingContext)
  const { ref, isDropTarget } = useDroppable({
    id: `${RIGHT_PREFIX}${item.value}`,
    data: item,
    disabled: isDisabled,
  })
  const ariaLabel = rtValueToText(item.label) || item.value

  return (
    <MatchingItem
      ariaLabel={ariaLabel}
      font={font}
      height={height}
      index={index}
      isActive={item.isActive}
      isDropTarget={isDropTarget}
      mode='right'
      ref={ref as React.RefCallback<HTMLDivElement>}
      styles={styles}
    >
      <div className={s.text}>
        <RichTextViewer styles={font} value={item.label || textToRtValue('')} />
      </div>
    </MatchingItem>
  )
}

const DefaultElementPreview: React.FC = () => {
  const {
    setDraggableId,
    right,
    left,
    elementId,
    block,
    validOrderMap,
    styles,
    value,
    feedback,
    font,
    mode,
  } = useContext(TestMatchingContext)
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.testMatching.type,
      elementId,
    },
  )
  const dispatch = useProjectDispatch()
  const ref = useRef<HTMLDivElement>(null)
  const state = useSize(ref) || { width: 1, height: 1 }
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const paddingsArr = (styles.indents.padding as string).split(' ')
  const paddings = parseInt(paddingsArr[0]) + parseInt(paddingsArr[2])
  const { height } = useGetSize(state, wrapperRef, value.items, paddings)
  const blockResult = useProjectContext(getBlockState, block?.uuid)?.result
  const feedbackValue = getFeedbackValue(value)
  const isPdf = mode.previewMode === PreviewMode.pdf
  const hasGeneralFb =
    value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.perOption
  const announce = useLiveFeedback('polite')

  useUpdateEffect(() => {
    if (!blockResult) {
      return
    }
    const correctPairs = left.map((leftItem) => {
      const correctIndex = validOrderMap[leftItem?.value]
      const correctRightItem = correctIndex !== undefined ? right[correctIndex] : undefined
      return `${rtValueToText(leftItem?.label)} - ${rtValueToText(correctRightItem?.label)}`
    })
    const yourPairs = left.map((leftItem, index) => {
      const rightItem = right[index]
      return `${rtValueToText(leftItem?.label)} - ${rtValueToText(rightItem?.label)}`
    })

    announce(
      buildTestAnnouncement(
        blockResult.isValid,
        correctPairs.length ? correctPairs.join(', ') : undefined,
        yourPairs.length ? yourPairs.join(', ') : undefined,
      ),
    )
  }, [blockResult])

  const onDragEnd = ({ operation, canceled }: Parameters<DragEndEvent>[0]) => {
    setDraggableId(undefined)
    if (canceled || !operation.source || !operation.target) {
      return
    }

    const sourceItemValue = String(operation.source.id)
    const targetRightValue = String(operation.target.id).replace(RIGHT_PREFIX, '')

    const sourceIndex = left.findIndex((e) => e.value === sourceItemValue)
    const destIndex = right.findIndex((e) => e.value === targetRightValue)

    if (sourceIndex === -1 || destIndex === -1) {
      return
    }

    let newLeftItems = produce(left, (draft) => {
      const destEl = draft[destIndex]
      if (destEl) {
        destEl.isActive = false
      }
      const sourceEl = draft[sourceIndex]
      if (sourceEl) {
        sourceEl.isActive = true
      }
    })

    newLeftItems = swapItems(newLeftItems, sourceIndex, destIndex)

    const draggableLabel = rtValueToText(left.find((e) => e.value === sourceItemValue)?.label)
    const droppableLabel = rtValueToText(right.find((e) => e.value === targetRightValue)?.label)

    const data = entityAnalyticsConfig.interacted({
      resultExtensions: {
        dragIndex: sourceIndex,
        dropIndex: destIndex,
        dragItem: sourceItemValue,
        dropItem: targetRightValue,
        labelDragItem: draggableLabel,
        labelDropItem: droppableLabel,
      },
      objectExtensions: baseObjectExtensions,
      elementId,
    })
    sendXapiData(data)

    const isReady = newLeftItems.every(({ isActive }) => isActive)
    dispatch(
      setQuestion({
        elementId,
        value: {
          value: newLeftItems.map((item) => item.value),
          shuffledItems: newLeftItems,
        },
        isReady,
        blockId: block?.uuid || '',
      }),
    )

    if (isReady) {
      announce('All pairs matched')
    }
  }

  return (
    <DragDropProvider
      onDragEnd={onDragEnd}
      onDragStart={({ operation }) =>
        setDraggableId(operation.source ? String(operation.source.id) : undefined)
      }
      plugins={(defaults) => [...defaults, createDndAccessibilityPlugin(getName)]}
    >
      <div ref={wrapperRef}>
        <div aria-label='Matching pairs' className={s.matchingElement} ref={ref} role='list'>
          {left.map((leftItem, index) => {
            const rightItem = right[index]
            return (
              <div
                key={index}
                role='listitem'
                style={{
                  height,
                  marginBottom:
                    value.items.length - 1 !== index ? styles.indents.marginBottom : undefined,
                }}
              >
                <TestFeedback
                  blockUuid={block?.uuid}
                  feedbackValue={feedbackValue}
                  font={font}
                  isTrueAnswer={validOrderMap[leftItem?.value] === index}
                  label={`${rtValueToText(leftItem?.label)} - ${rtValueToText(rightItem?.label)}`}
                  showAnswerFb={!!value.answerFeedback}
                  styles={feedback}
                >
                  <div className={s.row}>
                    <div className={s.item}>
                      <LeftPreviewItem height={height} index={index} item={leftItem} key={leftItem.value} />
                    </div>
                    <div style={{ flex: '0 0 24px' }} />
                    <div className={s.item}>
                      <RightPreviewItem height={height} index={index} item={rightItem} />
                    </div>
                  </div>
                </TestFeedback>
              </div>
            )
          })}
        </div>
      </div>
      <TestFeedback
        blockUuid={block?.uuid}
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={blockResult?.isValid}
        mode={mode}
        rtProps={{
          value: value.generalFeedback,
        }}
        showFeedback={isPdf ? hasGeneralFb : hasGeneralFb && !!blockResult}
        styles={feedback}
      />
    </DragDropProvider>
  )
}

export default DefaultElementPreview
